import { useMemo, useState } from 'react';
import type { GeoPoint } from '../../lib/api/analytics';

/**
 * Aggregates the same `points[]` payload that drives the heatmap into a
 * country-leaderboard. Reuses the existing `/admin/analytics/geo` request
 * so opening this card is free — the data is already in the React Query
 * cache, we just bucket it differently here.
 *
 * Column model:
 *   - Flag — emoji rendered from the ISO-A2 (regional indicator symbols)
 *   - Country — Intl.DisplayNames in the user's UA locale (English in the
 *     admin's case, but RTL-tolerant)
 *   - Cities — distinct city centroids the country appears under
 *   - Sessions — sum across the period
 *   - Devices — sum of distinct device-counts (city-level, so this is a
 *     ceiling — the same device travelling between two cities would be
 *     counted twice; for the share calculation that overestimate stays
 *     consistent column-wise so percentages still compose to ~100%)
 *   - Share — percentage of total devices in the dataset
 */

interface TopCountriesTableProps {
  points: GeoPoint[];
}

interface CountryRow {
  iso: string;
  name: string;
  cities: number;
  sessions: number;
  uniques: number;
}

const DEFAULT_LIMIT = 10;

const NAMES = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();

export function TopCountriesTable({ points }: TopCountriesTableProps): JSX.Element {
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo<CountryRow[]>(() => {
    const map = new Map<string, CountryRow>();
    for (const p of points) {
      if (!p.countryIso) continue;
      const iso = p.countryIso.toUpperCase();
      const existing = map.get(iso);
      if (existing) {
        existing.cities += 1;
        existing.sessions += p.sessions;
        existing.uniques += p.uniques;
      } else {
        map.set(iso, {
          iso,
          name: NAMES?.of(iso) ?? iso,
          cities: 1,
          sessions: p.sessions,
          uniques: p.uniques,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.uniques - a.uniques);
  }, [points]);

  const totalUniques = useMemo(
    () => rows.reduce((sum, r) => sum + r.uniques, 0),
    [rows],
  );
  const visible = showAll ? rows : rows.slice(0, DEFAULT_LIMIT);

  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-ink-subtle">
        No country-attributable sessions yet.
      </p>
    );
  }

  return (
    <div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-[10.5px] font-mono uppercase tracking-[0.14em] text-ink-subtle">
            <th className="w-8 py-2 text-left"></th>
            <th className="py-2 text-left">Country</th>
            <th className="py-2 pr-3 text-right">Cities</th>
            <th className="py-2 pr-3 text-right">Sessions</th>
            <th className="py-2 pr-3 text-right">Devices</th>
            <th className="w-24 py-2 pr-1 text-right">Share</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => {
            const share = totalUniques > 0 ? (r.uniques / totalUniques) * 100 : 0;
            return (
              <tr
                key={r.iso}
                className="border-b border-line-hair last:border-b-0 hover:bg-surface-muted"
              >
                <td className="py-2 text-base leading-none">{flagFromIso(r.iso)}</td>
                <td className="py-2">
                  <div className="text-ink">{r.name}</div>
                  <div className="font-mono text-[10.5px] uppercase tracking-wider text-ink-subtle">
                    {r.iso}
                  </div>
                </td>
                <td className="py-2 pr-3 text-right font-mono text-ink-muted">
                  {r.cities.toLocaleString()}
                </td>
                <td className="py-2 pr-3 text-right font-mono text-ink-muted">
                  {r.sessions.toLocaleString()}
                </td>
                <td className="py-2 pr-3 text-right font-mono font-semibold text-ink">
                  {r.uniques.toLocaleString()}
                </td>
                <td className="py-2 pr-1 text-right">
                  <ShareBar pct={share} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > DEFAULT_LIMIT ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 w-full rounded-md border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
        >
          {showAll
            ? `Show top ${DEFAULT_LIMIT}`
            : `Show all ${rows.length} countries`}
        </button>
      ) : null}
    </div>
  );
}

/** Inline mini bar — visual share without dragging in a chart library. */
function ShareBar({ pct }: { pct: number }): JSX.Element {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="font-mono text-[11px] tabular-nums text-ink-muted">
        {pct < 0.1 ? '<0.1%' : `${pct.toFixed(1)}%`}
      </span>
      <span className="relative inline-block h-1.5 w-12 overflow-hidden rounded-full bg-line-hair">
        <span
          className="absolute inset-y-0 left-0 bg-brand"
          style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
        />
      </span>
    </div>
  );
}

/** ISO-A2 → regional-indicator-symbol flag emoji. Each ASCII letter maps to
 *  a regional indicator codepoint at U+1F1E6 + (letter - 'A'). The pair
 *  renders as a flag glyph on every modern OS (Windows excepts via emoji
 *  fonts; in admin contexts that's a non-issue). Returns the bare letters
 *  as a fallback if the input is malformed. */
function flagFromIso(iso: string): string {
  if (!/^[A-Z]{2}$/.test(iso)) return iso;
  const A = 0x1f1e6;
  const offset = 'A'.charCodeAt(0);
  const codepoints = [...iso].map((ch) => A + (ch.charCodeAt(0) - offset));
  return String.fromCodePoint(...codepoints);
}
